import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';
// import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import tokenExpirationChecker from '../utils/tokenExpirationChecker';

// Create the Auth Context
export const AuthContext = createContext();

// Get the API URL from environment or localStorage
let API_URL = process.env.REACT_APP_API_URL;

// Check localStorage for stored API URL from previous attempts
const storedApiUrl = localStorage.getItem('api_url');
if (storedApiUrl) {
  API_URL = storedApiUrl;
} else {
  // Default to relative URL in production, localhost in development
  API_URL = API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
}

// Force default axios baseURL to use local API in development mode
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  API_URL = 'https://nivaas.app';
}
// Set default axios base URL for all axios calls
axios.defaults.baseURL = API_URL;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationContext, setOrganizationContext] = useState(null);
  const navigate = useNavigate();

  // Initialize organization context from localStorage on mount
  useEffect(() => {
    const storedOrgDomain = localStorage.getItem('organization_domain');
    if (storedOrgDomain) {
      setOrganizationContext(storedOrgDomain);
    }
  }, []);

  // Update organization context when currentUser changes
  useEffect(() => {
    if (currentUser?.organization_domain) {
      setOrganizationContext(currentUser.organization_domain);
      localStorage.setItem('organization_domain', currentUser.organization_domain);
    }
  }, [currentUser]);


  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor to add token to requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          // Only log for non-frequent requests to reduce console spam
          const isFrequentRequest = config.url?.includes('/images') ||
            config.url?.includes('/photo') ||
            config.url?.includes('/direct-photo') ||
            config.url?.includes('/direct-id-document') ||
            config.url?.includes('/api/auth/profile') ||
            config.url?.includes('/api/settings/public') ||
            config.url?.includes('/api/health') ||
            config.url?.includes('/uploads') ||
            config.url?.includes('/favicon');

          if (!isFrequentRequest) {
            console.log('Interceptor: Adding token to request headers');
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.log('Interceptor: Received 401/403 response, checking if we need to logout');

          // Don't logout on login attempts that fail
          if (error.config.url.includes('/api/auth/login')) {
            return Promise.reject(error);
          }

          // Check for JWT signature errors specifically
          const errorMessage = error.response?.data?.error || error.message || '';
          const isJWTSignatureError = errorMessage.includes('invalid signature') ||
            errorMessage.includes('jwt malformed') ||
            errorMessage.includes('invalid token');

          if (isJWTSignatureError) {
            console.log('JWT signature error detected - performing complete session cleanup');
            // Clear all possible storage locations
            localStorage.clear();
            sessionStorage.clear();

            // Clear axios headers
            delete axios.defaults.headers.common['Authorization'];

            // Reset user state
            setCurrentUser(null);
            setError('Your session has expired. Please log in again.');

            // Force redirect to login
            navigate('/login', { replace: true });
            return Promise.reject(error);
          }

          // Check if token exists but is invalid/expired
          const token = localStorage.getItem('token');
          if (token) {
            console.log('Token exists but API returned 401/403, clearing session');
            // Clear user session and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            setCurrentUser(null);

            // Redirect to login if not already there
            if (!window.location.pathname.includes('/login')) {
              navigate('/login', { replace: true });
            }
          }
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);

  // Reset axios default headers when component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Logout function wrapped in useCallback
  const logout = useCallback(() => {
    console.log('Logging out user');
    // Stop token expiration checker
    tokenExpirationChecker.stopChecking();
    // Clear all auth-related items from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization_domain');
    localStorage.removeItem('organization_name');
    localStorage.removeItem('organization_status');
    // Clear all auth-related items from sessionStorage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    // Remove auth header from axios
    delete axios.defaults.headers.common['Authorization'];
    // Reset state
    setCurrentUser(null);
    setError(null);
    // Navigate to login page
    navigate('/login', { replace: true });
  }, [navigate]);



  // Periodically check organization status for non-superadmin users
  useEffect(() => {
    // Skip for unauthenticated users or superadmins
    if (!currentUser || currentUser.role === 'superadmin') return;

    const checkOrganizationStatus = async () => {
      try {
        // Only check if we have a token and user is not a superadmin
        const token = localStorage.getItem('token');
        if (!token) return;

        // Directly check profile endpoint to verify organization status
        await axios.get(`${API_URL}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });

      } catch (error) {
        // Organization cancelled or other auth error
        if (error.response?.status === 403 && error.response?.data?.cancelled) {
          console.log('Periodic check: Organization membership cancelled, logging out');
          logout();
          setError('Your organization membership has been cancelled. Please contact the administrator.');
        }
      }
    };

    // Run immediately and then every 5 minutes
    checkOrganizationStatus();
    const interval = setInterval(checkOrganizationStatus, 5 * 60 * 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [currentUser, navigate, logout, setError]);

  // Generic error handler for API errors
  const handleApiError = useCallback((error, setErrorFunc) => {
    console.error('API Error:', error);

    // Handle specific error responses
    if (error.response) {
      // Handle token expiration or cancellation status
      if (error.response.status === 401 && error.response.data.expired) {
        console.log('Token expired, logging out');
        logout();
        setErrorFunc('Your session has expired. Please login again.');
        return;
      }

      // Handle cancelled organization membership
      if (error.response.status === 403 && error.response.data.cancelled) {
        console.log('Organization membership cancelled, logging out');
        logout();
        setErrorFunc('Your organization membership has been cancelled. Please contact the administrator.');
        return;
      }

      // Handle loginRequired flag (for auth middleware cancellations)
      if (error.response.data.loginRequired) {
        console.log('Login required due to organization status change');
        logout();
        setErrorFunc(error.response.data.message || 'Please login again');
        return;
      }

      setErrorFunc(error.response.data.message || 'An error occurred');
    } else if (error.request) {
      // The request was made but no response was received
      console.log('No response received:', error.request);
      setErrorFunc('No response from server. Please check your connection.');
    } else {
      // Something happened in setting up the request
      setErrorFunc('Error: ' + error.message);
    }
  }, [logout]);

  // Helper function to fetch organization name (MOVED ABOVE useEffect)
  const fetchOrganizationName = useCallback(async (userData) => {
    const tenantId = userData.tenant_id || userData.organization_id;
    if (!tenantId || userData.organization_name) return;

    try {
      // Try to get organization name from tenant details
      const tenantResponse = await axios.get(`${API_URL}/api/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (tenantResponse.data?.name) {
        localStorage.setItem('organization_name', tenantResponse.data.name);
        userData.organization_name = tenantResponse.data.name;
        sessionStorage.setItem('user', JSON.stringify(userData));
        return;
      }
    } catch (error) {
      console.warn('AuthContext: Failed to fetch tenant details:', error);
    }

    try {
      // Fallback to settings
      const settingsResponse = await axios.get(`${API_URL}/api/settings/public`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (settingsResponse.data?.society_name) {
        localStorage.setItem('organization_name', settingsResponse.data.society_name);
        userData.organization_name = settingsResponse.data.society_name;
        sessionStorage.setItem('user', JSON.stringify(userData));
        return;
      }
    } catch (error) {
      console.warn('AuthContext: Failed to fetch settings:', error);
    }

    // Use default name if all else fails
    if (userData.name) {
      const defaultOrgName = `${userData.name}'s Organization`;
      localStorage.setItem('organization_name', defaultOrgName);
      userData.organization_name = defaultOrgName;
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
  }, []);


  // ✅ Restore session on page refresh or navigation
  useEffect(() => {
    const restoreSession = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        if (!token) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // Always validate token with server on session restore 
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await validateTokenWithServer();
      } catch (error) {
        setCurrentUser(null);
        setLoading(false);
      }
    };

    // ✅ Separate function to validate token with server (non-recursive)
    const validateTokenWithServer = async () => {
      try {
        const response = await api.get('/api/auth/profile');

        // Update organization context if it came in the response
        if (response.data.organization_domain) {
          setOrganizationContext(response.data.organization_domain);
          localStorage.setItem('organization_domain', response.data.organization_domain);
        }

        // Save user to sessionStorage for quick restoration
        sessionStorage.setItem('user', JSON.stringify(response.data));

        // Fetch organization name if needed
        await fetchOrganizationName(response.data);
        setCurrentUser(response.data);
        setError(null);

        // Start token expiration checker
        tokenExpirationChecker.startChecking(() => {
          console.log('Token expired, logging out user');
          logout();
          setError('Your session has expired. Please log in again.');
        });
      } catch (error) {
        handleApiError(error, setError);
        setCurrentUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, [handleApiError, logout, fetchOrganizationName]);

  // Cleanup token expiration checker on unmount
  useEffect(() => {
    return () => {
      tokenExpirationChecker.stopChecking();
    };
  }, []);

  // Check token on page focus/visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, checking token...');
        tokenExpirationChecker.manualCheck(() => {
          console.log('Token expired on page focus, logging out');
          logout();
          setError('Your session has expired. Please log in again.');
        });
      }
    };

    const handleFocus = () => {
      console.log('Window gained focus, checking token...');
      tokenExpirationChecker.manualCheck(() => {
        console.log('Token expired on window focus, logging out');
        logout();
        setError('Your session has expired. Please log in again.');
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [logout]);

  // Function to check auth status
  const checkAuthStatus = async (organizationDomain = null) => {
    try {
      setLoading(true);

      const config = {};
      // Use provided domain, current context, or stored domain in that order
      const orgDomain = organizationDomain || organizationContext || localStorage.getItem('organization_domain');
      if (orgDomain) {
        config.headers = { 'X-Organization-Domain': orgDomain };
      }
      const response = await api.get('/api/auth/profile', config);

      // Update organization context if it came in the response
      if (response.data.organization_domain) {
        setOrganizationContext(response.data.organization_domain);
        localStorage.setItem('organization_domain', response.data.organization_domain);
      }

      // Save user to sessionStorage for quick restoration
      sessionStorage.setItem('user', JSON.stringify(response.data));

      // Fetch organization name if needed
      await fetchOrganizationName(response.data);

      setCurrentUser(response.data);
      setError(null);
      return response.data;
    } catch (error) {
      handleApiError(error, setError);
      setCurrentUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine dashboard URL based on user role
  const getDashboardUrl = useCallback((role) => {
    if (!role) return '/login';

    switch (role) {
      case 'superadmin':
        return '/superadmin/dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'owner':
        return '/owner/dashboard';
      case 'guard':
        return '/guard/dashboard';
      case 'maintenance':
        return '/maintenance/dashboard';
      default:
        return '/login';
    }
  }, []);

  // Login function
  const login = async (emailOrToken, password = null, organizationDomain = null) => {
    try {
      setLoading(true);

      // Check if first parameter is a token (for switch user functionality)
      if (typeof emailOrToken === 'string' && emailOrToken.length > 100 && !password) {
        // This is a token-based login (for switch user)
        const token = emailOrToken;

        // Store token
        localStorage.setItem('token', token);
        sessionStorage.setItem('token', token);

        // Set up axios defaults with the new token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Decode token to get user info (basic decode without verification)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userData = {
            id: payload.id,
            name: payload.name,
            email: payload.email,
            role: payload.role,
            organization_id: payload.organization_id,
            organization_domain: payload.organization_domain,
            isSwitchedUser: payload.isSwitchedUser,
            switchSession: payload.switchSession
          };

          // Store user data
          localStorage.setItem('user', JSON.stringify(userData));
          sessionStorage.setItem('user', JSON.stringify(userData));

          // Set organization context if available
          if (payload.organization_domain) {
            setOrganizationContext(payload.organization_domain);
            localStorage.setItem('organization_domain', payload.organization_domain);
          }

          console.log('Token-based login successful for user:', userData.email);
          setCurrentUser(userData);
          setError(null);

          // Start token expiration checker
          tokenExpirationChecker.startChecking(() => {
            console.log('Token expired, logging out user');
            logout();
            setError('Your session has expired. Please log in again.');
          });

          return userData;
        } catch (decodeError) {
          console.error('Error decoding token:', decodeError);
          throw new Error('Invalid token format');
        }
      } else {
        // This is a regular credential-based login
        const email = emailOrToken;

        // Clear any stored organization domain for regular logins to prevent conflicts
        localStorage.removeItem('organization_domain');
        sessionStorage.removeItem('organization_domain');
        setOrganizationContext(null);

        // Build request payload
        const requestData = { email, password };
        if (organizationDomain) {
          requestData.organization_domain = organizationDomain;
          setOrganizationContext(organizationDomain);
        }

        console.log(`Attempting login for ${email}${organizationDomain ? ` in organization ${organizationDomain}` : ''}`);
        console.log('Request data:', { ...requestData, password: '********' });

        // Execute login via api instance
        const response = await api.post('/api/auth/login', requestData);

        console.log('Login response:', response.data);

        if (response.data.token) {
          // Store token and user data
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          sessionStorage.setItem('token', response.data.token);
          sessionStorage.setItem('user', JSON.stringify(response.data.user));

          // Set up axios defaults with the new token
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

          console.log('Login successful for user:', response.data.user.email);

          // Set user data in state immediately without additional calls
          setCurrentUser(response.data.user);
          setError(null);

          // Start token expiration checker
          tokenExpirationChecker.startChecking(() => {
            console.log('Token expired, logging out user');
            logout();
            setError('Your session has expired. Please log in again.');
          });

          // Return the user data for navigation
          return response.data.user;
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Failed to login. Please check your credentials.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data
  const refreshUser = async () => {
    try {
      setLoading(true);
      const apiUrl = `${API_URL}/api/auth/profile`;
      console.log('Refreshing user data from API at:', apiUrl);

      // Add token debugging
      const token = localStorage.getItem('token');
      console.log('Token exists for refresh:', !!token);
      if (token) {
        console.log('Token first 10 chars:', token.substring(0, 10) + '...');
      } else {
        console.warn('No token found for user refresh');
      }

      // Set the Authorization header explicitly for this request
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('User refresh successful, updated user:', response.data);

      // Save updated user to sessionStorage
      sessionStorage.setItem('user', JSON.stringify(response.data));

      setCurrentUser(response.data);
      setError(null);
      return response.data;
    } catch (error) {
      console.error('User refresh error:', error.response?.data || error.message);
      console.error('Full error details:', error);
      // If we get a 401, the token is invalid - trigger a logout
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.warn('Auth error during refresh, logging out user');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setCurrentUser(null);
        setError('Your session has expired. Please login again.');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    checkAuthStatus,
    refreshUser,
    isAuthenticated: !!currentUser,
    organizationContext,
    setOrganizationContext,
    getDashboardUrl
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 